// components/slides/SlidesValidationCard.tsx
'use client';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DeckReviewState, SlideReviewVerdict } from '#types/types';
import { buildReviewStampLayout, stampDisplayText, type StampLayout, STAMP_LINE_HEIGHT } from '#lib/slides/stampLayout';
import { buildStampArtwork } from '#lib/slides/stampArtwork';
import { standardFontStyle } from '#lib/slides/standardText';
import { resolveAuthorityStatus, stampStrings, type SlidesValidationStatus } from '#lib/slides/validationPoints';
import { URW_LOGO_PNG_BASE64, CONCEPT_STUDIO_LOGO_PNG_BASE64 } from '#lib/slides/stampLogos';
import { useSlidesStore } from './slidesStore';
import styles from './SlidesValidationCard.module.css';

export const buildStampCardLayout = buildReviewStampLayout;
const VERDICT_CYCLE: SlideReviewVerdict[] = ['pass', 'fail', 'warn', 'na'];
const STATUS_CYCLE: SlidesValidationStatus[] = ['A', 'B', 'C'];

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

// Fitted lines are presentation only. Focus restores the source text so merely
// visiting a field cannot persist PDF transliterations or artificial wrapping.
const Editable = ({ value, displayValue = value, onCommit, style, label }: {
  value: string; displayValue?: string; onCommit: (v: string) => void; style?: React.CSSProperties; label: string;
}) => {
  const element = useRef<HTMLSpanElement>(null);
  const editing = useRef(false);
  const original = useRef(value);
  const clearPointer = useRef<(() => void) | null>(null);
  useLayoutEffect(() => () => clearPointer.current?.(), []);
  useLayoutEffect(() => {
    if (element.current && !editing.current) element.current.textContent = displayValue;
  }, [value, displayValue]);
  return (
  <span
    ref={element}
    contentEditable
    spellCheck={false}
    role="textbox"
    aria-label={label}
    suppressContentEditableWarning
    onPointerDown={(e) => {
      stop(e);
      if (editing.current || e.button !== 0) return;
      const node = e.currentTarget;
      const doc = node.ownerDocument;
      const caret = doc.caretPositionFromPoint?.(e.clientX, e.clientY);
      const range = caret ? doc.createRange() : doc.caretRangeFromPoint?.(e.clientX, e.clientY);
      if (caret && range) range.setStart(caret.offsetNode, caret.offset);
      if (!range || !node.contains(range.startContainer)) return;
      const prefix = range.cloneRange();
      prefix.selectNodeContents(node);
      prefix.setEnd(range.startContainer, range.startOffset);
      // Wrapping changes whitespace and PDF glyphs may expand (≥ becomes >=).
      let remaining = prefix.toString().replace(/\s/g, '').length;
      const afterSpace = prefix.toString().length === 0 || /\s$/.test(prefix.toString());
      let offset = 0;
      for (const character of value) {
        if (remaining <= 0 && (!afterSpace || /\S/.test(character))) break;
        remaining -= stampDisplayText(character).replace(/\s/g, '').length;
        offset += character.length;
      }
      const startX = e.clientX, startY = e.clientY;
      const clear = () => {
        doc.removeEventListener('pointerup', release);
        doc.removeEventListener('pointercancel', clear);
        clearPointer.current = null;
      };
      const release = (event: PointerEvent) => {
        clear();
        const selection = doc.getSelection();
        if (doc.activeElement !== node || !selection) return;
        const dragged = Math.hypot(event.clientX - startX, event.clientY - startY) > 3;
        const anchor = node.firstChild ?? node;
        // Leave native dragging intact; only correct the anchor that moved on focus.
        if (dragged && selection.focusNode) {
          selection.setBaseAndExtent(anchor, offset, selection.focusNode, selection.focusOffset);
        } else {
          selection.collapse(anchor, offset);
        }
      };
      clearPointer.current?.();
      clearPointer.current = clear;
      doc.addEventListener('pointerup', release, { once: true });
      doc.addEventListener('pointercancel', clear, { once: true });
    }}
    onClick={stop}
    onFocus={(e) => {
      editing.current = true;
      original.current = value;
      e.currentTarget.textContent = value;
    }}
    onBlur={(e) => {
      clearPointer.current?.();
      const node = e.currentTarget;
      const emptyFiller = node.childNodes.length === 1 && node.firstChild?.nodeName === 'BR';
      const raw = emptyFiller ? '' : node.innerText ?? node.textContent ?? '';
      const unchanged = raw.replace(/\r\n?/g, '\n') === original.current.replace(/\r\n?/g, '\n');
      const v = unchanged ? original.current : raw;
      editing.current = false;
      node.textContent = displayValue;
      if (v !== original.current) onCommit(v);
    }}
    onKeyDown={(e) => {
      stop(e);
      if (e.key === 'Escape') { e.preventDefault(); e.currentTarget.textContent = original.current; e.currentTarget.blur(); }
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
    }}
    className={styles.editable}
    style={{ outline: 'none', cursor: 'text', minWidth: 1, display: 'inline-block', fontSize: 'inherit', fontFamily: 'inherit', whiteSpace: 'pre', ...style }}
  />
  );
};


export function SlidesValidationCard({ projectId, review, width, height, fontScale, layout: layoutProp, artwork: artworkProp }: {
  projectId: string; review: DeckReviewState; width: number; height?: number;
  fontScale?: number; layout?: StampLayout;
  artwork?: ReturnType<typeof buildStampArtwork>;
}) {
  const round = useSlidesStore(s => s.decks[projectId]?.reviewRound ?? 1);
  const deckName = useSlidesStore(s => s.decks[projectId]?.name ?? '');
  const [editing, setEditing] = useState<{ projectId: string; field: string | null }>({ projectId, field: null });
  if (editing.projectId !== projectId) setEditing({ projectId, field: null });
  const layout = useMemo(() => layoutProp ?? buildStampCardLayout(review, width, height, fontScale, round, deckName),
    [layoutProp, review, width, height, fontScale, round, deckName]);
  const artwork = useMemo(() => artworkProp ?? buildStampArtwork(review, width, layout, height), [artworkProp, review, width, layout, height]);
  const commit = (key: string, value: string) => {
    const store = useSlidesStore.getState(), current = store.decks[projectId]?.doc.review;
    if (!current) return;
    const strings = stampStrings(current.language), status = current.status ?? 'C';
    if (key.startsWith('header:')) store.setReview(projectId, { header: { ...current.header, [key.slice(7)]: value } });
    // Typing the line's own sentence back is a reset, so the next colour change still swaps it.
    else if (key === 'authorities') store.setReview(projectId, { authorityText: value === strings.authorities[resolveAuthorityStatus(current.authorityStatus, status)] ? undefined : value });
    else if (key === 'status') store.setReview(projectId, { statusText: value === strings.status[status] ? undefined : value });
    else store.setReview(projectId, { points: { [key]: { ...current.points?.[key], verdict: current.points?.[key]?.verdict ?? 'warn', comment: value } } });
  };
  const cycle = (key: string) => {
    const store = useSlidesStore.getState(), current = store.decks[projectId]?.doc.review;
    if (!current) return;
    const status = current.status ?? 'C';
    if (key === 'status') store.setReviewStatus(projectId, STATUS_CYCLE[(STATUS_CYCLE.indexOf(status) + 1) % 3]!);
    else if (key === 'authorities') store.toggleAuthorityStatus(projectId);
    else {
      const point = current.points?.[key], index = VERDICT_CYCLE.indexOf(point?.verdict ?? 'warn');
      store.setReview(projectId, { points: { [key]: { ...point, verdict: VERDICT_CYCLE[(index + 1) % VERDICT_CYCLE.length]! } } });
    }
  };
  return (
    <div className={styles.card} data-stamp-card data-slides-tour-project={projectId} data-stamp-overflow={artwork.overflow || undefined}
      onPointerDown={stop} style={{ width, height: layout.height, position: 'relative' }}>
      <svg viewBox={`0 0 ${width} ${layout.height}`} width={width} height={layout.height} style={{ overflow: 'visible', position: 'absolute', pointerEvents: 'none' }}>
        {artwork.rects.map((r, i) => <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} fill={r.fill ?? 'none'} stroke={r.stroke} strokeWidth={r.strokeWidth ?? 0} />)}
        {artwork.paths.map((p, i) => <path key={i} d={p.d} fill="none" stroke={p.color} strokeWidth={p.width} />)}
        {artwork.texts.map((t, i) => <text key={i} aria-hidden={t.field ? true : undefined} x={t.x} y={t.y} fill={t.color} visibility={t.field === editing.field ? 'hidden' : undefined} textLength={t.width || undefined} lengthAdjust="spacingAndGlyphs"
          style={{ ...standardFontStyle(t.font), fontSize: t.size }}>{t.text}</text>)}
        {(['urw', 'cs'] as const).map(key => {
          const r = artwork.logos[key];
          return <image key={key} x={r.x} y={r.y} width={r.width} height={r.height} href={`data:image/png;base64,${key === 'urw' ? URW_LOGO_PNG_BASE64 : CONCEPT_STUDIO_LOGO_PNG_BASE64}`} />;
        })}
      </svg>
      {artwork.fields.map(f => <div key={`${projectId}:${f.key}`} onFocus={() => setEditing({ projectId, field: f.key })} onBlur={() => setEditing({ projectId, field: null })} style={{ position: 'absolute', left: f.x, top: f.y, width: f.w, height: f.h,
        display: 'flex', alignItems: 'center', fontSize: f.size, lineHeight: STAMP_LINE_HEIGHT }}>
        <Editable label={f.label} value={f.value} displayValue={f.display} onCommit={value => commit(f.key, value)} style={{ width: '100%', minHeight: f.size * STAMP_LINE_HEIGHT }} />
      </div>)}
      {artwork.cells.map(c => <button key={c.key} type="button" aria-label={`${c.label}: ${c.value}`}
        data-slides-tour={c.key === 'status' ? 'grade' : undefined}
        aria-description={c.description}
        title={`${c.label}: ${c.value}. Click to change`} className={styles.verdict}
        onClick={e => { stop(e); cycle(c.key); }} onKeyDown={stop}
        style={{ position: 'absolute', left: c.x, top: c.y, width: c.w, height: c.h }} />)}
    </div>
  );
}
